import { useCallback, useEffect } from 'react';
import { Button } from '@/components/ui';

export type MetaLinkErrorKind =
  | 'invalid_state'
  | 'state_expired_or_consumed'
  | 'meta_api_error'
  | 'setup_failed'
  | 'setup_indeterminate'
  | 'sync_failed'
  | 'verify_failed'
  | 'threshold_rejected'
  | 'currency_rejected'
  | 'already_connected'
  | 'gate'
  | 'unknown';

interface MetaLinkErrorProps {
  error: MetaLinkErrorKind;
  serverMessage?: string;
  /**
   * Whether retrying makes sense for this error. The parent decides — some
   * buckets (state-related ones, the gate) have nothing to retry from this
   * URL. Defaults to true so non-gate buckets keep their previous behavior;
   * gate is also hidden internally.
   */
  canRetry?: boolean;
  onRetry: () => void;
  onClose: () => void;
}

interface ErrorCopy {
  title: string;
  primary: string;
}

const ERROR_COPY: Record<MetaLinkErrorKind, ErrorCopy> = {
  invalid_state: {
    title: 'Invalid Meta Callback',
    primary:
      "We couldn't verify this Meta sign-in. Start over from your account page.",
  },
  state_expired_or_consumed: {
    title: 'Meta Session Expired',
    primary:
      'Your Meta connection session expired or has already been used. Start over from your account page.',
  },
  meta_api_error: {
    title: 'Meta API Error',
    primary:
      "Meta's API returned an error while fetching your ad accounts. Try again in a moment.",
  },
  setup_failed: {
    // Your account IS linked at this point — the failure was in the
    // backfill trigger downstream of linkAccount. We tell the user that
    // explicitly so they don't try to re-link.
    title: 'Setup Could Not Complete',
    primary:
      'Your Meta account was linked, but the initial data sync could not be completed. The next scheduled run will retry; you can also try again now.',
  },
  setup_indeterminate: {
    // The client aborted before hearing back from the server. The
    // aggregate-spend gate may still complete in the background and could
    // hard-delete the credential; we don't know yet. Send the user to
    // /account where they can see the actual live state instead of
    // guessing at "success" or "failure" from a timeout.
    title: 'Setup Status Unknown',
    primary:
      "Setup is taking longer than expected. We're not sure yet whether your Meta account is linked — check your Account page in a moment to confirm. If it isn't listed, try linking again.",
  },
  sync_failed: {
    // Backend returned 200+success=false with reason_kind='sync_failed':
    // Cloud Run ingest didn't populate data, so the aggregate-spend gate
    // couldn't run safely. Credential was hard-deleted server-side to
    // avoid permanently bypassing the gate. Retrying /complete-setup
    // would 404 (no credential), so the user must restart OAuth from
    // /account. Non-retryable at this URL; close→/account.
    title: 'Setup Could Not Complete',
    primary:
      'Your Meta data sync failed to complete, so we cleaned up the connection. Please try linking again from your Account page.',
  },
  verify_failed: {
    // Same shape as sync_failed: backend cleaned up the credential, so
    // retry-in-place would 404. User restarts from /account.
    title: 'Setup Could Not Complete',
    primary:
      "We couldn't verify this Meta ad account's currency with Meta, so we cleaned up the connection. Please try linking again from your Account page.",
  },
  gate: {
    title: 'Meta Integration Unavailable',
    primary: 'Meta integration is not available for your account.',
  },
  threshold_rejected: {
    // The backend aggregate-spend gate on /complete-setup returned
    // 200 with success=false. The credential row was hard-deleted so
    // the free-tier single-platform lock releases and the member can
    // try Google (or a different Meta ad account) — a retry against
    // this same Meta account would fail identically, so the parent
    // suppresses the Retry button and routes close→/account.
    title: 'Account Not Eligible',
    primary:
      'This Meta ad account does not meet the spend threshold for your tier. Try a different account, add spend to this one, or upgrade to Pro.',
  },
  currency_rejected: {
    // Distinct from threshold_rejected: the account uses a non-USD
    // currency (or Meta returned no currency). Terminal — retrying
    // this account would fail identically. Copilot round-19 #SSS.
    title: 'Account Currency Not Supported',
    primary:
      "This Meta ad account isn't denominated in USD, which is the only currency Flouri's free-tier thresholds currently support. Try a USD ad account, or contact support@flouri.ai about multi-currency support.",
  },
  already_connected: {
    // The ad account is already linked to another Flouri member. Race
    // with a concurrent link elsewhere — backend hard-deletes this
    // member's newly-created credential. Terminal.
    title: 'Account Already Connected',
    primary:
      'This Meta ad account is already connected to a different Flouri account. Choose a different ad account, or contact support@flouri.ai if you believe this is an error.',
  },
  unknown: {
    title: 'Unexpected Error',
    primary:
      'Something went wrong while connecting your Meta account. Try again, or contact support if it keeps happening.',
  },
};

/**
 * Bucketed error screen mirroring `GoogleAdsLinker/PollingError.tsx`. Meta
 * has fewer error buckets than Google because:
 *   - Meta's connect flow is synchronous in the callback (no polling
 *     timeouts).
 *   - There's no MCC link or health check (no `HealthCheckError` analog).
 *   - There's no email-confirmation step (Google's MCC invitation flow).
 *
 * The buckets here map cleanly to the HTTP responses from the Meta
 * endpoints in `src/api/routers/meta_ads_linking.py`.
 */
export function MetaLinkError({
  error,
  serverMessage,
  canRetry = true,
  onRetry,
  onClose,
}: MetaLinkErrorProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const copy = ERROR_COPY[error];
  const modalTitleId = 'meta-link-error-title';
  const isGate = error === 'gate';
  // Both gate and threshold_rejected are terminal-from-this-URL buckets:
  // no meaningful retry from the callback screen, and the natural next
  // action for the user is to try a different account from /account.
  // MetaOAuthCallback routes onClose→goToAccount for both; the button
  // label must agree, otherwise the CTA reads "Back to Dashboard" while
  // actually navigating to /account.
  const closesToAccount =
    error === 'gate' ||
    error === 'threshold_rejected' ||
    error === 'currency_rejected' ||
    error === 'already_connected' ||
    error === 'setup_indeterminate' ||
    error === 'sync_failed' ||
    error === 'verify_failed' ||
    error === 'unknown';
  const showRetry = !isGate && canRetry;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex flex-col items-center text-center">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h3
            id={modalTitleId}
            className="text-xl font-semibold text-gray-900 mb-2"
          >
            {copy.title}
          </h3>
          <p className="text-gray-700 mb-2">{copy.primary}</p>
          {serverMessage && (
            <p className="text-xs text-gray-500 mb-4 break-words">
              {serverMessage}
            </p>
          )}
          <div className="w-full flex flex-col gap-2 mt-2">
            {showRetry && (
              <Button
                variant="primary"
                size="lg"
                onClick={onRetry}
                className="w-full"
              >
                Try Again
              </Button>
            )}
            <Button
              variant={showRetry ? 'secondary' : 'primary'}
              size="lg"
              onClick={onClose}
              className="w-full"
            >
              {closesToAccount ? 'Return to Account' : 'Back to Dashboard'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
